import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { ProductContext } from "../../../store";
import { Row, Col, Button, Card, CardBody, Input } from "reactstrap";
import { toast } from "react-toastify";
import CBToolTip from "../../../components/CBToolTip";
import AiVisibilitySetupProgress from "../../../components/AiVisibility/AiVisibilitySetupProgress";
import {
	AI_VISIBILITY_INTEGRATION_KEY_LABELS,
	clearSetupWebsiteId,
	getValueForKey,
	readSetupWebsiteId,
} from "../../../components/AiVisibility/websiteKeysUtils";

const AiVisibilityComplete = ({
	collectPath = "/getStarted/aiVisibility",
}) => {
	const location = useLocation();
	const navigate = useNavigate();
	const { setCurrentWebsiteId } = useContext(ProductContext);
	const [copyTexts, setCopyTexts] = useState({ apiKey: "Copy" });
	const [setupFailed, setSetupFailed] = useState(false);
	const navigatedRef = useRef(false);

	const website = location.state?.website;
	const websiteKeys = location.state?.websiteKeys ?? [];
	const initialTaskStatus =
		location.state?.initialTaskStatus ?? location.state?.audit?.taskStatus;

	const websiteId = useMemo(() => {
		if (website?.websiteId != null) return website.websiteId;
		return readSetupWebsiteId();
	}, [website?.websiteId]);

	const websiteURL = website?.websiteURL || website?.websiteUrl || "";

	useEffect(() => {
		if (websiteId == null) {
			navigate(collectPath, { replace: true });
		}
	}, [websiteId, navigate, collectPath]);

	const activateWebsite = useCallback(() => {
		if (websiteId == null) return;
		Cookies.set("_website_id", String(websiteId));
		setCurrentWebsiteId(websiteId);
		clearSetupWebsiteId();
	}, [websiteId, setCurrentWebsiteId]);

	const handleSetupComplete = useCallback(() => {
		if (navigatedRef.current) return;
		navigatedRef.current = true;
		activateWebsite();
		navigate("/aivisibility/audit-list", { replace: true });
	}, [activateWebsite, navigate]);

	const handleSetupFailed = useCallback(() => {
		setSetupFailed(true);
	}, []);

	const handleCopy = (key, value) => {
		if (!value) return;
		navigator.clipboard.writeText(value).then(() => {
			setCopyTexts((prev) => ({ ...prev, [key]: "Copied" }));
			toast.success("Copied to clipboard");
			setTimeout(() => {
				setCopyTexts((prev) => ({ ...prev, [key]: "Copy" }));
			}, 2000);
		});
	};

	if (websiteId == null) return null;

	return (
		<>
			<section className="choosePlatformHeader">
				<div className="container">
					<Row className="align-items-center justify-content-center">
						<Col xs="auto">
							<h2 className="mt-5">AI Visibility Setup</h2>
							<p className="text-center text-muted">
								{setupFailed
									? "Setup could not be completed"
									: "Running your visibility audit"}
							</p>
						</Col>
					</Row>
				</div>
			</section>

			<section className="shopifyAppInstallationGuide mt-4 pb-5">
				<div className="container">
					<Row className="justify-content-center">
						<Col lg={8}>
							<Card className="mb-4 shadow-sm">
								<CardBody>
									<AiVisibilitySetupProgress
										websiteId={websiteId}
										initialTaskStatus={initialTaskStatus}
										onComplete={handleSetupComplete}
										onFailed={handleSetupFailed}
									/>
								</CardBody>
							</Card>

							{websiteURL && (
								<Card className="mb-4 shadow-sm">
									<CardBody>
										<h5 className="mb-2">Website</h5>
										<p className="text-muted mb-0">{websiteURL}</p>
									</CardBody>
								</Card>
							)}

							{AI_VISIBILITY_INTEGRATION_KEY_LABELS.map((keyConfig) => {
								const value = getValueForKey(websiteKeys, keyConfig.key);
								if (!value) return null;
								return (
									<Card key={keyConfig.key} className="mb-4 shadow-sm">
										<CardBody>
											<h5 className="mb-3">{keyConfig.label}</h5>
											<div className="relative maxW450">
												<Input
													type="text"
													readOnly
													value={value}
													style={{ fontFamily: "monospace", fontSize: "14px" }}
												/>
												<Button
													color="link"
													className="copyButton"
													onClick={() => handleCopy(keyConfig.key, value)}
												>
													<CBToolTip
														item={{
															placement: "top",
															text: copyTexts[keyConfig.key] || "Copy",
														}}
														id={`${keyConfig.key}Tooltip`}
													>
														<span className="material-icons" style={{ fontSize: 18 }}>
															content_copy
														</span>
													</CBToolTip>
												</Button>
											</div>
										</CardBody>
									</Card>
								);
							})}

							{setupFailed && (
								<div className="d-flex justify-content-center">
									<Button
										color="secondary"
										outline
										className="cta"
										onClick={() => navigate(collectPath, { replace: true })}
									>
										Back to setup
									</Button>
								</div>
							)}
						</Col>
					</Row>
				</div>
			</section>
		</>
	);
};

export default AiVisibilityComplete;
